import { Device } from '@goodboydigital/astro';
import { SignalHash } from '@goodboydigital/odie';
import { Container, InteractionEvent, Sprite, Texture } from 'pixi.js';
import { Signal } from 'typed-signals';

import { Game } from '../game/Game';
import { PointingArrow } from './PointingArrow';

export interface TouchControlOptions
{
    game: Game;
    icon?: Texture;
    base: Texture;
    baseDown?: Texture;
    iconScaleX?: number;
    iconScaleY?: number;
    viewOffsetX?: number;
    viewOffsetY?: number;
    baseAnchorX?: number;
    baseAnchorY?: number;
    baseDownAnchorX?: number;
    baseDownAnchorY?: number;
    keys: string[]
}

export class TouchControl
{
    public game: Game;
    public view: Container;
    public pressing = false;
    public enabled = false;
    public signals: SignalHash;

    private icon?: Sprite;
    private base: Sprite;
    private area: Sprite;

    private container: Container;
    private upTexture: Texture;
    private baseDown?: Texture;
    private viewOffsetX: number;
    private viewOffsetY: number;

    private baseAnchorX: number;
    private baseAnchorY: number;
    private baseDownAnchorX: number;
    private baseDownAnchorY: number;
    private keys: string[];
    private pointingArrow?: PointingArrow;
    private maxWidth = 450;

    constructor(options: TouchControlOptions)
    {
        this.view = new Container();
        this.game = options.game;

        this.area = Sprite.from(Texture.WHITE);
        this.area.anchor.set(0.5);
        this.area.alpha = 0.0;
        this.view.addChild(this.area);

        this.container = new Container();
        this.view.addChild(this.container);

        this.upTexture = options.base;
        this.baseDown = options.baseDown;

        this.viewOffsetX = options.viewOffsetX ?? 0;
        this.viewOffsetY = options.viewOffsetY ?? 0;
        this.baseAnchorX = options.baseAnchorX || 0.5;
        this.baseAnchorY = options.baseAnchorY || 0.5;
        this.baseDownAnchorX = options.baseDownAnchorX || this.baseAnchorX;
        this.baseDownAnchorY = options.baseDownAnchorY || this.baseAnchorY;
        this.keys = options.keys.slice(0);

        this.base = Sprite.from(options.base);
        this.base.anchor.set(this.baseAnchorX, this.baseAnchorY);
        this.container.addChild(this.base);

        if (options.icon)
        {
            this.icon = Sprite.from(options.icon);
            this.icon.anchor.set(0.5);
            this.container.addChild(this.icon);
            this.icon.scale.set(options.iconScaleX ?? 1, options.iconScaleY ?? 1);
        }

        this.signals = {};
        this.signals.pressed = new Signal<() => void>();
        this.signals.released = new Signal<() => void>();

        this.container.alpha = 0.25;
        this.view.visible = false;
    }

    public show(): void
    {
        if (!Device.desktop) this.view.visible = true;
        this.enable();
    }

    public hide(): void
    {
        this.view.visible = false;
        this.disable();
    }

    public enable(): void
    {
        if (this.enabled) return;
        this.enabled = true;
        this.area.interactive = true;
        this.area.on('pointerdown', this.handlePress, this);
        this.area.on('pointermove', this.handleMove, this);
        this.area.on('pointerup', this.handleRelease, this);
        this.area.on('pointerleave', this.handleRelease, this);
        this.area.on('pointerupoutside', this.handleRelease, this);
        this.area.on('pointerout', this.handleRelease, this);
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
        this.container.alpha = 1;
    }

    public disable(): void
    {
        if (!this.enabled) return;
        this.enabled = false;
        this.handleRelease();
        this.highlightOff();
        this.area.interactive = false;
        this.area.off('pointerdown', this.handlePress, this);
        this.area.off('pointermove', this.handleMove, this);
        this.area.off('pointerup', this.handleRelease, this);
        this.area.off('pointerleave', this.handleRelease, this);
        this.area.off('pointerupoutside', this.handleRelease, this);
        this.area.off('pointerout', this.handleRelease, this);
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
        this.container.alpha = 0.25;
    }

    public reset(): void
    {
        this.pressing = false;
        this.base.texture = this.upTexture;
    }

    public animatePress(): void
    {
        if (this.baseDown)
        {
            this.base.texture = this.baseDown;
            this.base.anchor.set(this.baseDownAnchorX, this.baseDownAnchorY);
        }
        else
        {
            this.container.scale.set(0.9);
        }
    }

    public animateRelease(): void
    {
        this.base.texture = this.upTexture;
        this.base.anchor.set(this.baseAnchorX, this.baseAnchorY);
        this.container.scale.set(1);
    }

    public resize(width?: number, height?: number): void
    {
        if (width) this.area.width = Math.min(width, this.maxWidth);
        if (height) this.area.height = height;
        this.container.x = (this.area.width * this.viewOffsetX);
        this.container.y = (this.area.height * this.viewOffsetY);
        if (this.pointingArrow)
        {
            this.pointingArrow.x = this.container.x;
            this.pointingArrow.y = this.container.y - (this.base.height * this.base.anchor.y) - 40;
        }
    }

    public highlightOn(): void
    {
        if (!this.pointingArrow) this.pointingArrow = new PointingArrow();
        this.view.addChild(this.pointingArrow);
        this.resize();
        this.pointingArrow.show();
    }

    public highlightOff(): void
    {
        if (!this.pointingArrow) return;
        this.pointingArrow.hide();
    }

    protected handlePress = (): void =>
    {
        if (this.pressing) return;
        this.pressing = true;
        this.signals.pressed.emit();
        this.animatePress();
    };

    protected handleRelease = (): void =>
    {
        if (!this.pressing) return;
        this.pressing = false;
        this.signals.released.emit();
        this.animateRelease();
    };

    /** This allows user to slide touch from one button to another, trigering press action */
    protected handleMove = (e: InteractionEvent): void =>
    {
        const mid = this.game.width * 0.5;

        // Prevent buttons on opposite side of screen reacting to touch move
        if (this.view.x > mid && e.data.global.x <= mid) return;
        if (this.view.x < mid && e.data.global.x >= mid) return;

        if (this.area.containsPoint(e.data.global))
        {
            this.handlePress();
        }
        else
        {
            this.handleRelease();
        }
    };

    private handleKeyDown = (e: KeyboardEvent) =>
    {
        if (e.repeat || !this.area.interactive) return;
        if (this.keys.includes(e.key)) this.handlePress();
    };

    private handleKeyUp = (e: KeyboardEvent) =>
    {
        if (this.keys.includes(e.key)) this.handleRelease();
    };

    get width(): number { return this.area.width; }
    get height(): number { return this.area.height; }
}
